// shell/components/table/columns/canvas-column.ts

import { CanvasColumnType, CanvasColumnValue } from '../types';
import { ColumnRenderer, CellRenderContext, CellUpdateType, CellRenderResult } from './column-base';

export class CanvasColumnRenderer<T = unknown> extends ColumnRenderer<T> {
    readonly supportsGrid = true;
    readonly supportsTable = false;

    constructor(private config: CanvasColumnType<T>) {
        super();
    }

    getGridCellClass(): string {
        return 'cs-column-canvas';
    }

    getTableCellClass(): string {
        return 'cs-column-canvas';
    }

    buildGridCell(ctx: CellRenderContext<T>, cellWrapper: HTMLElement): HTMLElement | null {
        const value = ctx.value as CanvasColumnValue | null;
        if (!value) return null;

        return this.buildCanvasElement(value);
    }

    private buildCanvasElement(value: CanvasColumnValue): HTMLElement {
        // If URL provided, use image rendering
        if (value.url) {
            const img = document.createElement('img');
            img.className = 'cs-column-canvas__img';
            img.src = value.url;
            img.width = value.width;
            img.height = value.height;
            if (this.config.maxHeight) img.style.maxHeight = `${this.config.maxHeight}px`;
            if (this.config.maxWidth) img.style.maxWidth = `${this.config.maxWidth}px`;
            return img;
        }

        // Try canvas with solid color
        const canvas = document.createElement('canvas');
        canvas.className = 'cs-column-canvas__canvas';
        canvas.width = value.width;
        canvas.height = value.height;

        const ctx2d = canvas.getContext('2d');
        if (ctx2d && value.color) {
            ctx2d.fillStyle = value.color;
            ctx2d.fillRect(0, 0, value.width, value.height);
            return canvas;
        }

        // Fallback to colored div if canvas context unavailable
        if (value.color) {
            const div = document.createElement('div');
            div.className = 'cs-column-canvas__fallback';
            div.style.backgroundColor = value.color;
            div.style.width = `${value.width}px`;
            div.style.height = `${value.height}px`;
            div.style.aspectRatio = `${value.width} / ${value.height}`;
            if (this.config.maxHeight) div.style.maxHeight = `${this.config.maxHeight}px`;
            if (this.config.maxWidth) div.style.maxWidth = `${this.config.maxWidth}px`;
            return div;
        }

        // Empty placeholder
        const placeholder = document.createElement('div');
        placeholder.className = 'cs-column-canvas__placeholder';
        return placeholder;
    }

    buildTableCell(): HTMLElement | null {
        return null; // Not supported
    }

    updateGridCell(ctx: CellRenderContext<T>, cellWrapper: HTMLElement, contentEl: HTMLElement | null, type: CellUpdateType): CellRenderResult {
        // For canvas, always rebuild on full update (handles row recycling)
        if (type === CellUpdateType.Full) {
            if (contentEl) {
                contentEl.remove();
            }

            const value = ctx.value as CanvasColumnValue | null;
            if (!value) {
                return { contentElement: null };
            }

            const newContent = this.buildCanvasElement(value);
            cellWrapper.appendChild(newContent);
            return { contentElement: newContent };
        }

        return { contentElement: contentEl };
    }

    updateTableCell(ctx: CellRenderContext<T>, td: HTMLElement, contentEl: HTMLElement | null, type: CellUpdateType): CellRenderResult {
        // Not supported
        return { contentElement: null };
    }
}
